import { useEffect, useState } from "react";

import { dataApi } from "apis/dataV1";
import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { explorerActions, selectBackendProvider, selectConfig } from "slices/explorer";
import { UxFlavor } from "slices/explorer/types";
import { telemetry } from "telemetry";
import { NOVORENDER_CLIENT_ID } from "utils/auth/authenticateNovolib";
import { isStrictLibraryBuild } from "utils/buildType";
import { initMixpanel } from "utils/mixpanel";

export enum ConfigInitStatus {
    Initial,
    Loading,
    Ready,
}

const isLib = isStrictLibraryBuild();

export function useInitConfig() {
    const config = useAppSelector(selectConfig);
    const backendProvider = useAppSelector(selectBackendProvider);

    const dispatch = useAppDispatch();

    const [configStatus, setConfigStatus] = useState(ConfigInitStatus.Initial);

    useEffect(() => {
        initConfig();

        let timeOutId = 0;
        async function initConfig() {
            if (configStatus !== ConfigInitStatus.Initial) {
                return;
            }

            setConfigStatus(ConfigInitStatus.Loading);

            let attempt = 0;
            const load = () =>
                fetch("/config.json")
                    .then((res) => {
                        if (!res.ok) {
                            throw new Error(`HTTP ${res.status}`);
                        }
                        return res.json();
                    })
                    .catch(async (error) => {
                        if (attempt < 2) {
                            return new Promise((resolve) => {
                                timeOutId = window.setTimeout(
                                    async () => {
                                        ++attempt;
                                        resolve(await load());
                                    },
                                    Math.pow(2, attempt) * 1000,
                                );
                            });
                        }
                        // After retries exhausted, log warning but don't fail
                        console.warn("Failed to load /config.json after retries:", error);
                        telemetry.trackCaughtException({ source: "hooks.useInitConfig.load", category: "init", error });
                        return null;
                    });

            let finalConfig = config;
            if (import.meta.env.MODE === "development" || backendProvider === "procore") {
                // Skip config.json fetch for development mode or Procore backend
                // Procore backend uses runtime config from Redux, not static config.json
                dataApi.serviceUrl = config.dataV2ServerUrl;
            } else if (isLib) {
                dataApi.serviceUrl = config.dataV2ServerUrl;
                if (config.novorenderClientId !== NOVORENDER_CLIENT_ID) {
                    dispatch(
                        explorerActions.setConfig({
                            ...config,
                            novorenderClientId: NOVORENDER_CLIENT_ID,
                        }),
                    );
                }
            } else {
                const cfg = await load();
                if (cfg) {
                    dispatch(explorerActions.setConfig(cfg));
                    finalConfig = cfg;
                }

                dataApi.serviceUrl = cfg?.dataV2ServerUrl ?? config.dataV2ServerUrl;
            }

            const forceMobile = new URLSearchParams(window.location.search).has("forceMobile");
            if (forceMobile) {
                dispatch(explorerActions.setUxFlavor(UxFlavor.ProcoreAndroid));
            } else if (finalConfig.uxFlavor) {
                dispatch(explorerActions.setUxFlavor(finalConfig.uxFlavor as UxFlavor));
            }

            initMixpanel(finalConfig.mixpanelToken);
            setConfigStatus(ConfigInitStatus.Ready);
        }

        return () => {
            clearTimeout(timeOutId);
        };
    }, [dispatch, configStatus, config, backendProvider]);

    return configStatus;
}
